/**
 * Segnalazioni API Queries
 * Centralized data fetching for Segnalazioni feature
 */

import { supabase, getCurrentUserId } from '../client';
import type { 
  Segnalazione, SegnalazioneInsert, SegnalazioneUpdate,
  QueryOptions 
} from '@/types';

const TABLE = 'segnalazioni';

// ============================================================
// GET Queries
// ============================================================

export async function getSegnalazioni(options?: QueryOptions & { userId?: string }) {
  const userId = options?.userId || await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  let query = supabase
    .from(TABLE)
    .select('*, strutture(nome)', { count: options?.count })
    .eq('user_id', userId);

  // Apply filters
  if (options?.filters?.eq) {
    Object.entries(options.filters.eq).forEach(([key, value]) => {
      query = query.eq(key, value);
    });
  }

  // Apply ordering
  if (options?.order) {
    query = query.order(options.order.column, { 
      ascending: options.order.ascending ?? true 
    });
  } else {
    query = query.order('created_at', { ascending: false });
  }

  // Apply pagination
  if (options?.limit) {
    query = query.limit(options.limit);
  }
  if (options?.offset) {
    query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
  }

  const { data, error, count } = await query;

  return {
    data: data as (Segnalazione & { strutture: { nome: string } | null })[] | null,
    error,
    count,
  };
}

export async function getSegnalazioneById(id: string, userId?: string) {
  const uid = userId || await getCurrentUserId();
  if (!uid) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .select('*, strutture(*)')
    .eq('id', id)
    .eq('user_id', uid)
    .single();

  return {
    data: data as (Segnalazione & { strutture: unknown }) | null,
    error,
  };
}

// ============================================================
// POST/PUT/DELETE Queries
// ============================================================

export async function createSegnalazione(segnalazione: SegnalazioneInsert) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .insert({ ...segnalazione, user_id: userId })
    .select('*, strutture(nome)')
    .single();

  return {
    data: data as (Segnalazione & { strutture: { nome: string } | null }) | null,
    error,
  };
}

export async function updateSegnalazione(id: string, updates: SegnalazioneUpdate) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { data, error } = await supabase
    .from(TABLE)
    .update(updates)
    .eq('id', id)
    .eq('user_id', userId)
    .select('*, strutture(nome)')
    .single();

  return {
    data: data as (Segnalazione & { strutture: { nome: string } | null }) | null,
    error,
  };
}

export async function deleteSegnalazione(id: string) {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');
  
  const { error } = await supabase
    .from(TABLE)
    .delete()
    .eq('id', id)
    .eq('user_id', userId);

  return { error };
}
